import { useQuery } from '@tanstack/react-query';
import apiClient from '../api/apiClient';

interface Publisher {
  id: number;
  identity_type: string;
  name: string;
  display_name: string;
  national_id: string;
  address: string;
  postal_code: string;
  phone: string;
  status: string;
  active: number;
  national_id_image?: string | null;
  contract?: string | null;
  avatar?: string | null;
  errors?: any;
  /**
   * Identity edits awaiting review, keyed by column; only the changed fields.
   * The plain fields above stay the live identity the store shows. A requested
   * conversion to حقوقی arrives here as `type: 'LEGAL'`.
   */
  draft?: Record<string, any> | null;
  /**
   * Replacement files waiting for review; null when there is none. The live
   * urls above keep pointing at what a reviewer verified.
   */
  pending_avatar?: string | null;
  pending_national_id_image?: string | null;
  pending_contract?: string | null;
  /**
   * The type after the pending review — differs from identity_type only while
   * a conversion to LEGAL is waiting. Forms and labels follow this one.
   */
  proposed_identity_type?: 'LEGAL' | 'PERSONAL' | string;
  has_pending_changes?: boolean;
  published_applications_count?: number | null;
}

interface ApiResponse {
  publisher: Publisher;
}

const fetchPublisher = async (publisherId: number) => {
  const response = await apiClient.get<ApiResponse>(
    `/v2/publisher/show/${publisherId}`,
  );
  return response.data.publisher; // Return the nested `publisher` object
};

const usePublisher = (publisherId: number) => {
  return useQuery<Publisher, Error>({
    queryKey: ['publisher', publisherId],
    queryFn: () => fetchPublisher(publisherId),
    staleTime: 0, // Data is considered stale immediately
    refetchOnMount: true, // Refetch when component mounts
    refetchOnWindowFocus: true, // Refetch when window gains focus
  });
};

export default usePublisher;
